import React, { useState, useMemo, useRef } from 'react';
import { formatNameForDisplay } from '../../utils/nameUtils';
import { getPlayerNameColor } from '../../utils/playerUtils';
import { getGenderTextColor, getGenderAvatarBg } from '../../utils/genderColors';
import { Button, StatusBadge, MobileSearchField, ConfirmationModal } from '../common';
import PlayerInfoModal from './PlayerInfoModal';
import { getPlayerAvatarPublicUrl } from '../../services/avatarService';
import {
  canRemovePlayerFromSession,
} from '../../utils/playerUtils';

/**
 * Social Player Manager - Same tab structure as competitive session
 * Tabs: All, Playing, Waiting, Exited, Available
 */
const SocialPlayerManager = ({
  players = [],
  sessionPlayerStatus = {},
  sessionPlayerExitReasons = {},
  togglePlayerStatus,
  onAddNewPlayer,
  currentClubId,
  onGuestUpdated,
  sessionId = null,
  playersOnActiveCourts = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showPlayerInfoModal, setShowPlayerInfoModal] = useState(false);
  const [selectedPlayerForInfo, setSelectedPlayerForInfo] = useState(null);
  const [removeModalPlayer, setRemoveModalPlayer] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const searchInputRef = useRef(null);

  const isOnActiveCourt = (playerId) =>
    playersOnActiveCourts && typeof playersOnActiveCourts.has === 'function' && playersOnActiveCourts.has(playerId);

  const canRemoveFromSession = (player) =>
    canRemovePlayerFromSession({
      player,
      isOnCourt: isOnActiveCourt(player.id),
    });

  const getPlayerDisplayName = (player) =>
    player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim();

  const handleConfirmRemove = async () => {
    if (!removeModalPlayer) return;
    setRemoveLoading(true);
    try {
      const success = await togglePlayerStatus(removeModalPlayer.id, 'remove');
      if (success !== false) {
        setRemoveModalPlayer(null);
      }
    } finally {
      setRemoveLoading(false);
    }
  };

  // Filter and sort players (same logic as competitive: All, Playing, Waiting, Exited, Available)
  const filteredAndSortedPlayers = useMemo(() => {
    const playerName = (p) => (p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim()).toLowerCase();
    let filtered = players.filter(player => {
      const matchesSearch = playerName(player).includes(searchTerm.toLowerCase());
      const status = sessionPlayerStatus[player.id];
      const exitReason = sessionPlayerExitReasons[player.id];
      let matchesStatus = true;

      if (activeTab === 'all') {
        matchesStatus = status !== undefined;
      } else if (activeTab === 'playing') {
        matchesStatus = status === 'active';
      } else if (activeTab === 'waiting') {
        matchesStatus = status === 'signed_up' || status === 'paused';
      } else if (activeTab === 'exited') {
        matchesStatus =
          (status === 'inactive' && exitReason === 'finished') ||
          status === 'cancelled' ||
          (status === 'inactive' && exitReason === 'no_show');
      } else if (activeTab === 'available') {
        matchesStatus = status === undefined;
      }

      return matchesSearch && matchesStatus;
    });

    filtered.sort((a, b) => {
      let aValue, bValue;
      if (sortBy === 'name') {
        aValue = (a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim()).toLowerCase();
        bValue = (b.name || `${b.first_name || ''} ${b.last_name || ''}`.trim()).toLowerCase();
      } else {
        aValue = sessionPlayerStatus[a.id] || 'not_added';
        bValue = sessionPlayerStatus[b.id] || 'not_added';
      }
      if (sortOrder === 'asc') return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    });
    return filtered;
  }, [players, searchTerm, activeTab, sortBy, sortOrder, sessionPlayerStatus, sessionPlayerExitReasons]);

  // Counts aligned with competitive: All, Playing, Waiting, Exited, Available
  const playerCounts = useMemo(() => {
    const counts = { all: 0, playing: 0, waiting: 0, exited: 0, available: 0 };
    players.forEach(player => {
      const status = sessionPlayerStatus[player.id];
      const exitReason = sessionPlayerExitReasons[player.id];
      if (status === undefined) {
        counts.available++;
      } else {
        counts.all++;
        if (status === 'active') counts.playing++;
        else if (status === 'signed_up' || status === 'paused') counts.waiting++;
        else if (
          (status === 'inactive' && exitReason === 'finished') ||
          status === 'cancelled' ||
          (status === 'inactive' && exitReason === 'no_show')
        ) {
          counts.exited++;
        }
      }
    });
    return counts;
  }, [players, sessionPlayerStatus, sessionPlayerExitReasons]);

  const getStatusBadge = (status, exitReason) => {
    if (status === 'active') {
      return <StatusBadge variant="success" size="small">Playing</StatusBadge>;
    } else if (status === 'signed_up') {
      return <StatusBadge variant="primary" size="small">Signed Up</StatusBadge>;
    } else if (status === 'paused') {
      return <StatusBadge variant="warning" size="small">Paused</StatusBadge>;
    } else if (status === 'cancelled') {
      return <StatusBadge variant="error" size="small">Cancelled</StatusBadge>;
    } else if (status === 'inactive' && exitReason === 'no_show') {
      return <StatusBadge variant="warning" size="small">No Show</StatusBadge>;
    } else if (status === 'inactive' && exitReason === 'finished') {
      return <StatusBadge variant="info" size="small">Finished</StatusBadge>;
    } else {
      return <StatusBadge variant="ghost" size="small">Not Signed Up</StatusBadge>;
    }
  };

  const tabs = [
    { key: 'all', label: 'All', count: playerCounts.all },
    { key: 'playing', label: 'Playing', count: playerCounts.playing },
    { key: 'waiting', label: 'Waiting', count: playerCounts.waiting },
    { key: 'exited', label: 'Exited', count: playerCounts.exited },
    { key: 'available', label: 'Available', count: playerCounts.available }
  ];

  const openPlayerInfoModal = (player) => {
    setSelectedPlayerForInfo(player);
    setShowPlayerInfoModal(true);
  };

  const closePlayerInfoModal = () => {
    setShowPlayerInfoModal(false);
    setSelectedPlayerForInfo(null);
  };

  return (
    <div className="bg-surface rounded-lg border border-border-medium shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-border-medium">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Player Management</h3>
            <p className="text-sm text-text-secondary mt-1">
              Manage player statuses for this session
            </p>
          </div>
          <Button
            onClick={onAddNewPlayer}
            variant="primary"
            size="small"
          >
            + Add Player
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border-medium">
        <nav className="-mb-px flex items-center space-x-8 overflow-x-auto px-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary hover:border-border-medium'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === tab.key
                    ? 'bg-primary/15 text-primary'
                    : 'bg-surface-subtle text-text-secondary'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Search and Sort Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between p-4 border-b border-border-medium">
        <div className="flex-1 w-full sm:max-w-md">
          <MobileSearchField
            inputRef={searchInputRef}
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-border-medium rounded-md bg-surface text-text-primary focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-text-secondary">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-border-medium rounded-md bg-surface text-text-primary focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="name">Name</option>
            <option value="status">Status</option>
          </select>
          <button
            type="button"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-2 border border-border-medium rounded-md hover:bg-surface-subtle text-text-primary"
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Player List */}
      <div className="p-4">
        {filteredAndSortedPlayers.length === 0 ? (
          <div className="text-center py-8 text-text-muted">
            <p>No players found in this category.</p>
            {activeTab === 'available' && (
              <p className="text-sm mt-2">All club players are already signed up.</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredAndSortedPlayers.map((player) => {
              const status = sessionPlayerStatus[player.id];
              const exitReason = sessionPlayerExitReasons[player.id];
              
              return (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-3 bg-surface-subtle rounded-lg hover:bg-surface-elevated transition-colors border border-border-medium"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm overflow-hidden ${getGenderAvatarBg(player.gender)}`}
                    >
                      {player.avatar_url ? (
                        <img
                          src={getPlayerAvatarPublicUrl(player.avatar_url)}
                          alt={(player.name || player.first_name || '?')}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        (player.name || player.first_name || '?').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${getGenderTextColor(player.gender)}`}>
                          {formatNameForDisplay(player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim(), { maxLength: 20, useInitials: false }).display}
                        </span>
                        {getStatusBadge(status, exitReason)}
                      </div>
                      {player.is_guest && (
                        <span className="text-xs text-text-muted">Guest Player</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => openPlayerInfoModal(player)}
                      className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-elevated rounded-md transition-colors border border-border-medium"
                    >
                      View
                    </button>
                    
                    {/* Available actions based on current status */}
                    {status === 'active' && (
                      <>
                        <button
                          type="button"
                          onClick={() => togglePlayerStatus(player.id, 'paused')}
                          className="px-3 py-1.5 text-sm text-warning hover:text-warning-dark hover:bg-warning-light rounded-md transition-colors border border-warning/40"
                        >
                          Pause
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePlayerStatus(player.id, 'inactive', 'finished')}
                          className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-subtle rounded-md transition-colors border border-border-medium"
                        >
                          Finished
                        </button>
                        {canRemoveFromSession(player) && (
                          <button
                            type="button"
                            onClick={() => setRemoveModalPlayer(player)}
                            className="px-3 py-1.5 text-sm text-danger hover:text-danger-dark hover:bg-danger-light rounded-md transition-colors border border-danger/40"
                          >
                            Remove
                          </button>
                        )}
                      </>
                    )}
                    
                    {status === 'signed_up' && (
                      <>
                        <button
                          type="button"
                          onClick={() => togglePlayerStatus(player.id, 'active')}
                          className="px-3 py-1.5 text-sm text-success hover:text-success-dark hover:bg-success-light rounded-md transition-colors border border-success/40"
                        >
                          Mark as Playing
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePlayerStatus(player.id, 'inactive', 'no_show')}
                          className="px-3 py-1.5 text-sm text-warning hover:text-warning-dark hover:bg-warning-light rounded-md transition-colors border border-warning/40"
                        >
                          No Show
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePlayerStatus(player.id, 'cancelled', 'cancelled')}
                          className="px-3 py-1.5 text-sm text-danger hover:text-danger-dark hover:bg-danger-light rounded-md transition-colors border border-danger/40"
                        >
                          Cancelled
                        </button>
                        {canRemoveFromSession(player) && (
                          <button
                            type="button"
                            onClick={() => setRemoveModalPlayer(player)}
                            className="px-3 py-1.5 text-sm text-danger hover:text-danger-dark hover:bg-danger-light rounded-md transition-colors border border-danger/40"
                          >
                            Remove
                          </button>
                        )}
                      </>
                    )}
                    
                    {status === 'paused' && (
                      <>
                        <button
                          type="button"
                          onClick={() => togglePlayerStatus(player.id, 'active')}
                          className="px-3 py-1.5 text-sm text-success hover:text-success-dark hover:bg-success-light rounded-md transition-colors border border-success/40"
                        >
                          Resume
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePlayerStatus(player.id, 'signed_up')}
                          className="px-3 py-1.5 text-sm text-primary hover:opacity-90 hover:bg-primary/15 rounded-md transition-colors border border-primary/40"
                        >
                          Mark as Signed Up
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePlayerStatus(player.id, 'inactive', 'finished')}
                          className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-subtle rounded-md transition-colors border border-border-medium"
                        >
                          Finished
                        </button>
                        {canRemoveFromSession(player) && (
                          <button
                            type="button"
                            onClick={() => setRemoveModalPlayer(player)}
                            className="px-3 py-1.5 text-sm text-danger hover:text-danger-dark hover:bg-danger-light rounded-md transition-colors border border-danger/40"
                          >
                            Remove
                          </button>
                        )}
                      </>
                    )}
                    
                    {status === undefined && (
                      <button
                        type="button"
                        onClick={() => togglePlayerStatus(player.id, 'signed_up')}
                        className="px-3 py-1.5 text-sm text-primary hover:opacity-90 hover:bg-primary/15 rounded-md transition-colors border border-primary/40"
                      >
                        Add to Session
                      </button>
                    )}
                    
                    {(status === 'cancelled' || (status === 'inactive' && exitReason === 'no_show')) && (
                      <button
                        type="button"
                        onClick={() => togglePlayerStatus(player.id, 'signed_up')}
                        className="px-3 py-1.5 text-sm text-primary hover:opacity-90 hover:bg-primary/15 rounded-md transition-colors border border-primary/40"
                      >
                        Re-add
                      </button>
                    )}
                    
                    {status === 'inactive' && exitReason === 'finished' && (
                      <button
                        type="button"
                        onClick={() => togglePlayerStatus(player.id, 'signed_up')}
                        className="px-3 py-1.5 text-sm text-primary hover:opacity-90 hover:bg-primary/15 rounded-md transition-colors border border-primary/40"
                      >
                        Re-add
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Player Info Modal */}
      {showPlayerInfoModal && selectedPlayerForInfo && (
        <PlayerInfoModal
          isOpen={showPlayerInfoModal}
          onClose={closePlayerInfoModal}
          player={selectedPlayerForInfo}
          currentClubId={currentClubId}
          onGuestUpdated={onGuestUpdated}
          sessionId={sessionId}
        />
      )}

      <ConfirmationModal
        isOpen={!!removeModalPlayer}
        onClose={() => setRemoveModalPlayer(null)}
        onConfirm={handleConfirmRemove}
        title="Remove from session"
        message={
          removeModalPlayer
            ? `Remove ${getPlayerDisplayName(removeModalPlayer)} from this session? They will return to the available list.`
            : ''
        }
        confirmText="Remove"
        isDangerous
        loading={removeLoading}
      />
    </div>
  );
};

export default SocialPlayerManager;

